import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { useBranding } from "../lib/branding";
import { HardHat, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const branding = useBranding();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", expertise: "" });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get("/auth/registration-status")
      .then((r) => {
        setStatus(r.data);
        if (!r.data?.can_register) {
          toast.error("Inscription publique désactivée");
          setTimeout(() => navigate("/login", { replace: true }), 1500);
        }
      })
      .catch(() => navigate("/login", { replace: true }));
  }, [navigate]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success(status?.bootstrap_available ? "Super administrateur créé !" : "Compte créé !");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  if (!status) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Chargement…</div>;
  }
  if (!status.can_register) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="w-full max-w-md bg-white p-8 rounded-md border border-slate-200 text-center">
          <ShieldCheck size={36} className="text-btp-navy mx-auto mb-3" />
          <h2 className="font-heading text-xl font-bold mb-2">Inscription publique désactivée</h2>
          <p className="text-sm text-slate-500 mb-6">Les comptes sont gérés par votre super administrateur.</p>
          <Link to="/login" className="inline-block px-4 py-2 bg-btp-navy text-white rounded-md text-sm font-semibold">Retour à la connexion</Link>
        </div>
      </div>
    );
  }

  const isBootstrap = !!status.bootstrap_available;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md bg-white p-8 rounded-md border border-slate-200">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-md bg-btp-terra flex items-center justify-center overflow-hidden">
            {branding.company_logo_url ? (
              <img src={branding.company_logo_url} alt="logo" className="w-full h-full object-cover" />
            ) : (
              <HardHat size={20} className="text-white" />
            )}
          </div>
          <div>
            <div className="font-heading font-bold text-xl" data-testid="brand-name-register">{branding.company_name}</div>
            <div className="text-xs text-slate-500">{isBootstrap ? "Créer le super administrateur" : "Créer un compte client"}</div>
          </div>
        </div>

        {isBootstrap && (
          <div className="mb-5 p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800">
            Aucun utilisateur n&apos;existe. Ce premier compte sera <strong>super administrateur</strong> et pourra gérer la plateforme.
          </div>
        )}

        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Nom complet</label>
            <input required value={form.full_name} onChange={update("full_name")} className="mt-1.5 w-full h-11 px-3 border border-slate-300 rounded-md text-sm focus:border-btp-navy focus:outline-none focus:ring-1 focus:ring-btp-navy" data-testid="register-name" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Email</label>
            <input type="email" required value={form.email} onChange={update("email")} className="mt-1.5 w-full h-11 px-3 border border-slate-300 rounded-md text-sm focus:border-btp-navy focus:outline-none focus:ring-1 focus:ring-btp-navy" data-testid="register-email" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Mot de passe</label>
            <input type="password" required minLength={6} value={form.password} onChange={update("password")} className="mt-1.5 w-full h-11 px-3 border border-slate-300 rounded-md text-sm focus:border-btp-navy focus:outline-none focus:ring-1 focus:ring-btp-navy" data-testid="register-password" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Expertise (facultatif)</label>
            <input value={form.expertise} onChange={update("expertise")} placeholder="Géotechnicien…" className="mt-1.5 w-full h-11 px-3 border border-slate-300 rounded-md text-sm focus:border-btp-navy focus:outline-none focus:ring-1 focus:ring-btp-navy" data-testid="register-expertise" />
          </div>
          <button disabled={loading} className="w-full h-11 bg-btp-terra hover:bg-btp-terra-hover disabled:opacity-60 text-white rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2" data-testid="register-submit">
            {loading ? <Loader2 size={16} className="animate-spin" /> : (isBootstrap ? "Créer le super admin" : "Créer le compte")}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-slate-500">
          Déjà un compte ? <Link to="/login" className="text-btp-navy font-semibold hover:underline" data-testid="link-login">Se connecter</Link>
        </div>
      </div>
    </div>
  );
}
